// Distinct card types for the tickets section.
// Each type has its own visual identity and data shape.

// ── Request Card (customer requests, inbound, outbound) ──

const _statusColor = (status) => ({
  open: 'blue', new: 'blue', 'in-progress': 'green', scheduled: 'green',
  triaged: 'orange', sent: 'caption', draft: 'caption', resolved: 'green',
  published: 'green', 'in-review': 'orange'
}[status] || 'caption')

export const RequestCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'A',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  background: 'overlay',
  cursor: 'pointer',
  transition: 'border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease',
  ':hover': { borderColor: 'title', transform: 'translateY(-2px)', boxShadow: '0 8px 24px -8px var(--color-shadow)' },
  onClick: (e, el, s) => { if (s.root) s.root.update({ selectedTicket: s }) },

  Top: {
    flow: 'x',
    align: 'flex-start space-between',
    gap: 'Z',

    Title: {
      tag: 'h3', margin: '0', fontSize: 'A', fontWeight: '600',
      lineHeight: '1.3', color: 'title', flex: '1',
      text: (el, s) => s.title || ''
    },
    StatusBadge: {
      tag: 'span', fontSize: 'Y1', fontWeight: '700', padding: 'V Z',
      round: 'C', textTransform: 'uppercase', letterSpacing: '.04em',
      color: (el, s) => _statusColor(s.status),
      background: (el, s) => `color-mix(in srgb, var(--color-${_statusColor(s.status)}) 15%, transparent)`,
      text: (el, s) => (s.status || '').replace('-', ' ')
    }
  },

  Summary: {
    hide: (el, s) => !s.summary,
    tag: 'p', margin: '0', fontSize: 'Z2', lineHeight: '1.5',
    color: 'caption', overflow: 'hidden',
    webkitLineClamp: '2', webkitBoxOrient: 'vertical', display: '-webkit-box',
    text: (el, s) => s.summary || ''
  },

  Meta: {
    flow: 'x', gap: 'Z', align: 'center', wrap: 'wrap', marginTop: 'V',

    Author: {
      tag: 'span', fontSize: 'Y2', color: 'caption',
      text: (el, s) => s.author || s.recipient || ''
    },
    Channel: {
      hide: (el, s) => !s.channel,
      tag: 'span', fontSize: 'Y1', fontWeight: '600', padding: 'V Z',
      round: 'C', background: 'overlay', color: 'caption',
      text: (el, s) => s.channel || ''
    },
    Votes: {
      hide: (el, s) => !s.votes,
      tag: 'span', fontSize: 'Y2', color: 'blue', fontWeight: '600',
      text: (el, s) => s.votes ? `${s.votes} votes` : ''
    },
    Date: {
      tag: 'span', fontSize: 'Y2', color: 'caption',
      text: (el, s) => s.createdAt || ''
    }
  },

  Tags: {
    hide: (el, s) => !(s.tags && s.tags.length),
    flow: 'x', gap: 'Y', wrap: 'wrap', marginTop: 'V',
    childExtends: 'RequestTag',
    childrenAs: 'state',
    children: (el, s) => (s.tags || []).map(t => ({ label: t }))
  }
}

export const RequestTag = {
  tag: 'span', fontSize: 'Y1', fontWeight: '600', padding: 'V Z',
  round: 'C', background: 'blue.1', color: 'blue',
  text: (el, s) => s.label || ''
}

// ── Suggestion Card (analytics-driven improvements) ──

export const SuggestionCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'A',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  borderLeftWidth: '3px',
  borderLeftColor: (el, s) => s.impact === 'high' ? 'green' : s.impact === 'medium' ? 'orange' : 'caption',
  background: 'overlay',
  cursor: 'pointer',
  transition: 'border-color 150ms ease',
  ':hover': { borderColor: 'title', boxShadow: '0 8px 24px -8px var(--color-shadow)' },
  onClick: (e, el, s) => { if (s.root) s.root.update({ selectedTicket: s }) },

  Top: {
    flow: 'x', align: 'flex-start space-between', gap: 'Z',

    Title: {
      tag: 'h3', margin: '0', fontSize: 'A', fontWeight: '600',
      lineHeight: '1.3', color: 'title', flex: '1',
      text: (el, s) => s.title || ''
    },
    Confidence: {
      tag: 'span', fontSize: 'Y2', fontWeight: '700', color: 'green',
      text: (el, s) => s.confidence ? `${Math.round(s.confidence * 100)}% conf.` : ''
    }
  },

  Summary: {
    tag: 'p', margin: '0', fontSize: 'Z2', lineHeight: '1.5', color: 'caption',
    text: (el, s) => s.summary || ''
  },

  Meta: {
    flow: 'x', gap: 'Z', align: 'center', marginTop: 'V',

    Impact: {
      tag: 'span', fontSize: 'Y1', fontWeight: '700', padding: 'V Z',
      round: 'C', textTransform: 'uppercase',
      color: (el, s) => s.impact === 'high' ? 'green' : s.impact === 'medium' ? 'orange' : 'caption',
      background: (el, s) => {
        const c = s.impact === 'high' ? 'green' : s.impact === 'medium' ? 'orange' : 'caption'
        return `color-mix(in srgb, var(--color-${c}) 15%, transparent)`
      },
      text: (el, s) => `${s.impact || 'low'} impact`
    },
    Source: {
      tag: 'span', fontSize: 'Y2', color: 'caption',
      text: (el, s) => s.source || ''
    },
    Date: {
      tag: 'span', fontSize: 'Y2', color: 'caption',
      text: (el, s) => s.createdAt || ''
    }
  }
}

// ── Improvement Request Card (Freestyler) ──

const _modelLabel = (m) => ({ 'claude-haiku': 'Claude', 'gemini-3.1': 'Gemini' }[m] || m || '')

export const ImprovementCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'A',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  background: 'overlay',
  cursor: 'pointer',
  transition: 'border-color 150ms ease',
  ':hover': { borderColor: 'title', boxShadow: '0 8px 24px -8px var(--color-shadow)' },
  onClick: (e, el, s) => { if (s.root) s.root.update({ selectedTicket: s }) },

  Top: {
    flow: 'x', align: 'flex-start space-between', gap: 'Z',

    Title: {
      tag: 'h3', margin: '0', fontSize: 'A', fontWeight: '600',
      lineHeight: '1.3', color: 'title', flex: '1',
      text: (el, s) => s.title || ''
    },
    StatusBadge: {
      tag: 'span', fontSize: 'Y1', fontWeight: '700', padding: 'V Z',
      round: 'C', textTransform: 'uppercase',
      color: (el, s) => s.status === 'approved' ? 'green' : s.status === 'rejected' ? 'red' : 'orange',
      background: (el, s) => {
        const c = s.status === 'approved' ? 'green' : s.status === 'rejected' ? 'red' : 'orange'
        return `color-mix(in srgb, var(--color-${c}) 15%, transparent)`
      },
      text: (el, s) => s.status || ''
    }
  },

  Prompt: {
    tag: 'p', margin: '0', fontSize: 'Z2', lineHeight: '1.5', color: 'caption',
    fontStyle: 'italic',
    text: (el, s) => s.prompt ? `"${s.prompt}"` : ''
  },

  Meta: {
    flow: 'x', gap: 'Z', align: 'center', marginTop: 'V',

    Model: {
      tag: 'span', fontSize: 'Y1', fontWeight: '600', padding: 'V Z',
      round: 'C', background: 'overlay', color: 'caption',
      text: (el, s) => _modelLabel(s.model)
    },
    Author: {
      tag: 'span', fontSize: 'Y2', color: 'caption',
      text: (el, s) => s.author || ''
    },
    Date: {
      tag: 'span', fontSize: 'Y2', color: 'caption',
      text: (el, s) => s.createdAt || ''
    }
  }
}

// ── Edit Request Card (Pull Requests) ──

export const EditRequestCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'A',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  background: 'overlay',
  cursor: 'pointer',
  transition: 'border-color 150ms ease',
  ':hover': { borderColor: 'title', boxShadow: '0 8px 24px -8px var(--color-shadow)' },
  onClick: (e, el, s) => { if (s.root) s.root.update({ selectedTicket: s }) },

  Top: {
    flow: 'x', align: 'flex-start space-between', gap: 'Z',

    Title: {
      tag: 'h3', margin: '0', fontSize: 'A', fontWeight: '600',
      lineHeight: '1.3', color: 'title', flex: '1',
      text: (el, s) => s.title || ''
    },
    StatusBadge: {
      tag: 'span', fontSize: 'Y1', fontWeight: '700', padding: 'V Z',
      round: 'C', textTransform: 'uppercase',
      color: (el, s) => s.status === 'merged' ? 'purple' : s.status === 'open' ? 'green' : 'caption',
      background: (el, s) => {
        const c = s.status === 'merged' ? 'purple' : s.status === 'open' ? 'green' : 'caption'
        return `color-mix(in srgb, var(--color-${c}) 15%, transparent)`
      },
      text: (el, s) => s.status || ''
    }
  },

  Branch: {
    flow: 'x', gap: 'Z', align: 'center',

    Repo: {
      tag: 'span', fontSize: 'Y2', color: 'caption',
      text: (el, s) => s.repo || ''
    },
    BranchName: {
      tag: 'code', fontSize: 'Y2', color: 'blue', fontWeight: '600',
      padding: 'V Z', round: 'Y', background: 'blue.08',
      text: (el, s) => s.branch || ''
    }
  },

  Meta: {
    flow: 'x', gap: 'Z', align: 'center', marginTop: 'V',

    Diff: {
      flow: 'x', gap: 'Y',
      Additions: { tag: 'span', fontSize: 'Y2', color: 'green', fontWeight: '600', text: (el, s) => s.additions ? `+${s.additions}` : '' },
      Deletions: { tag: 'span', fontSize: 'Y2', color: 'red', fontWeight: '600', text: (el, s) => s.deletions ? `-${s.deletions}` : '' }
    },
    Author: {
      tag: 'span', fontSize: 'Y2', color: 'caption',
      text: (el, s) => s.author || ''
    },
    Reviewers: {
      hide: (el, s) => !(s.reviewers && s.reviewers.length),
      tag: 'span', fontSize: 'Y2', color: 'caption',
      text: (el, s) => `${(s.reviewers || []).length} reviewer${(s.reviewers || []).length === 1 ? '' : 's'}`
    },
    Date: {
      tag: 'span', fontSize: 'Y2', color: 'caption',
      text: (el, s) => s.createdAt || ''
    }
  }
}

// ── Audit Report Card ──

export const AuditCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'A',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  background: 'overlay',
  cursor: 'pointer',
  transition: 'border-color 150ms ease',
  ':hover': { borderColor: 'title', boxShadow: '0 8px 24px -8px var(--color-shadow)' },
  onClick: (e, el, s) => { if (s.root) s.root.update({ selectedTicket: s }) },

  Top: {
    flow: 'x', align: 'flex-start space-between', gap: 'Z',

    Title: {
      tag: 'h3', margin: '0', fontSize: 'A', fontWeight: '600',
      lineHeight: '1.3', color: 'title', flex: '1',
      text: (el, s) => s.title || ''
    },
    TypeBadge: {
      tag: 'span', fontSize: 'Y1', fontWeight: '700', padding: 'V Z',
      round: 'C', textTransform: 'uppercase', letterSpacing: '.04em',
      background: 'overlay', color: 'caption',
      text: (el, s) => s.type || ''
    }
  },

  Summary: {
    tag: 'p', margin: '0', fontSize: 'Z2', lineHeight: '1.5', color: 'caption',
    text: (el, s) => s.summary || ''
  },

  Meta: {
    flow: 'x', gap: 'Z', align: 'center', marginTop: 'V',
    StatusBadge: {
      tag: 'span', fontSize: 'Y1', fontWeight: '700', padding: 'V Z',
      round: 'C', textTransform: 'uppercase',
      color: (el, s) => _statusColor(s.status),
      background: (el, s) => `color-mix(in srgb, var(--color-${_statusColor(s.status)}) 15%, transparent)`,
      text: (el, s) => s.status || ''
    },
    Date: {
      tag: 'span', fontSize: 'Y2', color: 'caption',
      text: (el, s) => s.createdAt || ''
    }
  }
}
